///include /assets/js/app/20i/allowance-service.js

"use strict"

class ServicesInfo {
    static _allowancesPromise = null
    static _packagesCache = {}

    /**
     * Fetches all four allowance types in parallel via /a/grouped-multi.
     *
     * @returns {Promise<{autoscale_allowance: allowanceServiceIn[], managed_vps: allowanceServiceIn[], package_allowance: allowanceServiceIn[], wordpress_allowance: allowanceServiceIn[]}>}
     */
    static fetchAllAllowancesCombined() {
        if (this._allowancesPromise) {
            return this._allowancesPromise
        }
        const allowanceFields = [
            "id", "name", "cachedHostname", "count", "expiryDate",
            "packages.hasWebsiteTurbo", "packages.websiteTurbo",
            "productSpec", "packageIds", "serviceType", "timelineService",
            "packages.id", "packages.name", "packages.names", "packages.packageTypeName",
            "packages.created", "profileIdCache",
        ]
        const q = $.param({ fields: allowanceFields })
        const multiRequests = [
            `/a/service/autoscale_allowance?${q}`,
            `/a/service/managed_vps?${q}`,
            `/a/service/package_allowance?${q}`,
            `/a/service/wordpress_allowance?${q}`,
        ]
        const multiEncoded = encodeURIComponent(btoa(JSON.stringify(multiRequests)))

        this._allowancesPromise = $.ajax(`/a/grouped-multi?requests=${multiEncoded}`)
            .then(([autoscale_allowance, managed_vps, package_allowance, wordpress_allowance]) => {
                const result = {
                    autoscale_allowance: autoscale_allowance || [],
                    managed_vps:         managed_vps         || [],
                    package_allowance:   package_allowance   || [],
                    wordpress_allowance: wordpress_allowance || [],
                }
                this._allowancesPromise = Promise.resolve(result)
                return result
            }).catch(err => {
                this._allowancesPromise = null
                throw err
            })
        return this._allowancesPromise
    }

    constructor(makeInitialCalls = true) {
        this._forLocation = "*"
        this._forPackageLabel = ""
        this._forMultiplePackageLabels = ""
        this._forUser = null
        this._forPackageTypeName = null
        this._forSsl = null
        this._forEnabled = null
        this._forTurbo = null
        this._forPlatform = null
        this._forProductSpecCloud = null
        this._forDomain = null
        this._page = 0
        this._searchTerm = ""
        this._fetchGeneration = 0
        /** @type {?JQuery.jqXHR} Our own most recent fetchPackages() request - see fetch(). */
        this._pendingPackagesXhr = null
        this._sortAscending = true
        this._sortField = "name"

        this._startDate = null
        this._endDate = null

        this.byType = {
            /** @type {?AutoscaleAllowanceService[]} */
            autoscale_allowance: null,
            /** @type {?ManagedVpsService[]} */
            managed_vps: null,
            /** @type {?PackageService[]} */
            package: null,
            /** @type {?PackageAllowanceService[]} */
            package_allowance: null,
            /** @type {?WordpressAllowanceService[]} */
            wordpress_allowance: null,
        }
        /** @type {?PackageService[]} */
        this.allowancePackages = []
        this.nonAllowancePackages = null
        this.packageCount = null
        this._pageLength = 100
        this.servicePackageCount = null
        /** @type {?Store} */
        this.store               = null;

        // JB - makeInitialCalls added to prevent WordPress Manager from making these calls too early/unnecessarily
        // but not modifying how the object works in other places i.e. manage hosting/breadcrumbs
        if (makeInitialCalls) {
            this.fetch()
            this.fetchAllAllowances()
        }
    }

    get pageLength() {
        return this._pageLength;
    }

    set pageLength(v) {
        this._pageLength = +v;
        this.fetch();
    }

    /**
     * @type {?string}
     */
    get forLocation() {
        return this._forLocation
    }
    set forLocation(dcLocation) {
        this._forLocation = dcLocation
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    /**
     * @type {?string}
     */
    get forUser() {
        return this._forUser
    }
    set forUser(user) {
        this._forUser = user
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forPackageLabel() {
        return this._forPackageLabel
    }
    set forPackageLabel(label) {
        this._forPackageLabel = label
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forMultiplePackageLabels() {
        return this._forMultiplePackageLabels
    }
    set forMultiplePackageLabels(labels) {
        this._forMultiplePackageLabels = labels
        this.fetch()
    }

    get forPackageTypeName() {
        return this._forPackageTypeName
    }
    set forPackageTypeName(packageTypeName) {
        if(packageTypeName == "Cloud Server") {
            this._forPackageTypeName = "Managed VPS"
        }
        this._forPackageTypeName = packageTypeName
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forSsl() {
        return this._forSsl
    }
    set forSsl(state) {
        this._forSsl = state
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forEnabled() {
        return this._forEnabled
    }
    set forEnabled(state) {
        this._forEnabled = state
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forTurbo() {
        return this._forTurbo
    }
    set forTurbo(state) {
        this._forTurbo = state
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forPlatform() {
        return this._forPlatform
    }
    set forPlatform(platform) {
        this._forPlatform = platform
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forProductSpecCloud() {
        return this._forProductSpecCloud
    }
    set forProductSpecCloud(state) {
        this._forProductSpecCloud = state
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get forDomain() {
        return this._forDomain
    }
    set forDomain(state) {
        this._forDomain = state
        // Reset to the first page: the filtered result may have fewer pages than
        // the page the user was on, which would otherwise show an empty list.
        this.fetch(true, true)
    }

    get startDate() {
        return this._startDate
    }
    set startDate(d) {
        this._startDate = d
        this.fetch()
    }

    get endDate() {
        return this._endDate
    }
    set endDate(d) {
        this._endDate = d
        this.fetch()
    }

    get hasNonAllowancePackages() {
        if(this.packageCount === null) return null
        return !!(this.packageCount > 0)
    }

    /**
     * True when any header filter or the search box narrows the /a/package
     * request. Used to decide whether the full managed-VPS list may be appended
     * to nonAllowancePackages: it may only be appended for the unfiltered view,
     * otherwise it would show packages the request deliberately filtered out.
     *
     * @type {boolean}
     */
    get hasActivePackageFilter() {
        return(
            this.searchTerm != "" ||
            this.forLocation != "*" ||
            this.forSsl !== null ||
            this.forEnabled !== null ||
            this.forTurbo !== null ||
            this.forDomain !== null ||
            this.forPlatform !== null ||
            this.forProductSpecCloud !== null ||
            !!this.startDate ||
            !!this.endDate ||
            !!this.forPackageLabel ||
            !!this.forMultiplePackageLabels
        )
    }

    get hasPackages() {
        return(
            this.hasNonAllowancePackages ||
            (
                this.byType.package_allowance &&
                !!this.byType.package_allowance.length
            ) ||
            (
                this.byType.wordpress_allowance &&
                !!this.byType.wordpress_allowance.length
            ) ||
            (
                this.byType.managed_vps &&
                !!this.byType.managed_vps.length
            )
        )
    }

    get packageRequestBody() {
        /** @type {*[]} */
        const selectors = []
        selectors.push({
            $or: [
                {
                    packageBundleTypeId: {
                        $regex: "."
                    },
                },
                {
                    productSpec: "web-free",
                },
                {
                    productSpec: null,
                },
            ],
        })

        if (this.forLocation != "*") {
            selectors.push({
                "location": {
                    $regex: `${this.forLocation}`
                }
            })
        }

        if(this.searchTerm != "") {
            const regex = this.searchTerm.replace(/[.]/g, "[.]")
            selectors.push({
                $or: [
                    {
                        name: {
                            $regex: regex,
                        },
                    },
                    {
                        names: {
                            $elemMatch: {
                                $regex: regex,
                            },
                        },
                    }
                ],
            })
        }

        if(this.forSsl !== null) {
            selectors.push({
                hasSslCache: {
                    $regex: this.forSsl ? "1" : "0"
                }
            })
        }

        if(this.forEnabled !== null) {
            selectors.push({
                enabledCache: {
                    $regex: this.forEnabled ? "1" : "0"
                }
            })
        }
        if(this.forTurbo !== null) {
            selectors.push({
                hasWebsiteTurbo: {
                    $regex: this.forTurbo ? "1" : "0"
                }
            })
        }

        if(this.forDomain !== null) {
            selectors.push({
                hasDomainCache: {
                    $regex: this.forDomain ? "1" : "0"
                }
            })
        }

        if(this.forPlatform !== null || this.forProductSpecCloud) {
            const platformSelector = this.forPlatform !== null ? {
                web: {
                    $elemMatch: {
                        platform: {
                            $regex: this.forPlatform
                        }
                    }
                }
            } : null

            const cloudSelector = this.forProductSpecCloud ? {
                productSpec: {
                    $regex: "^cloud-"
                }
            } : null

            selectors.push(
                platformSelector && cloudSelector ?
                    { $or: [platformSelector, cloudSelector] } :
                    (platformSelector || cloudSelector)
            )
        }

        if(this.startDate) {
            selectors.push({
                createdAt: {
                    $gt: this.startDate
                }
            })
        }

        if(this.endDate) {
            selectors.push({
                createdAt: {
                    $lt: this.endDate
                }
            })
        }

        if(this.forMultiplePackageLabels) {
            for (const l of this.forMultiplePackageLabels) {
                selectors.push({
                    packageLabel: {
                        $elemMatch: {
                            labelId: l
                        }
                    }
                })
            }
        }

        if(this.forPackageLabel) {
            if(this.forPackageLabel === "-") {
                // "No Label" — match packages that have no label assigned.
                // Mirrors the "No StackCP user" filter: negate an $elemMatch
                // against any element that has a labelId ($regex "." = any value,
                // the codebase idiom for "field is present", since $exists is unsupported).
                selectors.push({
                    packageLabel: {
                        $not: {
                            $elemMatch: {
                                labelId: {
                                    $regex: "."
                                }
                            }
                        }
                    }
                })
            } else {
                selectors.push({
                    packageLabel: {
                        $elemMatch: {
                            labelId: this.forPackageLabel
                        }
                    }
                })
            }
        }

        if(this.forPackageTypeName && this.forPackageTypeName != "Cloud Server") {
            selectors.push({
                packageTypeName: {
                    $regex: `^${this.escapeRegexString(this.forPackageTypeName)}`
                },
                $not: {
                    productSpec: {
                        $regex: `^cloud-`
                    }
                },
            })
        }
        if(this.forPackageTypeName == "Cloud Server") {
            selectors.push({
                $and: [
                    {
                        packageTypeName: {
                            $regex: `Managed VPS`
                        },
                    },
                    {
                        productSpec: {
                            $regex: `^cloud-`
                        }
                    },
                ],
            })
        }


        if(this.forUser !== null) {
            if(this.forUser && this.forUser !== "*") {
                selectors.push({
                    accessControl: {
                        $elemMatch: {
                            userTuple: this.forUser
                        },
                    }
                })
            } else if(this.forUser === "*") {
                selectors.push({
                    accessControl: {
                        $elemMatch: {
                            userTuple: {
                                $regex: "^stack-user:",
                            }
                        }
                    }
                })
            } else {
                selectors.push({
                    accessControl: {
                        $not: {
                            $elemMatch: {
                                userTuple: {
                                    $regex: "^stack-user:",
                                },
                            },
                        },
                    }
                })
            }
        }

        return {
            selector: selectors.length == 1 ?
                selectors[0] :
                {$and: selectors},
        }
    }

    get page() {
        return this._page
    }
    set page(v) {
        this._page = v
        this.fetch(false)
    }

    /**
     * @type {string}
     */
    get searchTerm() {
        return this._searchTerm
    }
    set searchTerm(v) {
        this._searchTerm = v
        this.fetch()
    }

    get serviceHasNonAllowancePackages() {
        if(this.servicePackageCount === null) return null
        return !!this.servicePackageCount
    }

    /**
     * @type {?boolean} True if the reseller or similar has any packages (rather
     * than the selected user)
     */
    get serviceHasPackages() {
        return(
            this.serviceHasNonAllowancePackages ||
            (
                this.byType.package_allowance &&
                !!this.byType.package_allowance.length
            ) ||
            (
                this.byType.autoscale_allowance &&
                !!this.byType.autoscale_allowance.length
            ) ||
            (
                this.byType.wordpress_allowance &&
                !!this.byType.wordpress_allowance.length
            ) ||
            (
                this.byType.managed_vps &&
                !!this.byType.managed_vps.length
            )
        )
    }

    /** @type {boolean} */
    get sortAscending() {
        return this._sortAscending
    }
    set sortAscending(v) {
        if(v != this._sortAscending) {
            this._sortAscending = v
            this.fetch(false)
        }
    }

    /**
     * @type {string}
     */
    get sortField() {
        return this._sortField
    }
    set sortField(v) {
        if(v != this._sortField) {
            this._sortField = v
            this._sortAscending = true
            this.fetch(false)
        }
    }

    /**
     *
     * @param {boolean} include_meta
     */
    async fetch(include_meta = true, non_skip = false) {
        // Search now fires a fetch() on every keystroke, with no debounce - overlapping
        // calls are the norm while typing, and a slower earlier request can resolve
        // after a newer one. The generation counter lets us tell whether *this* call
        // is still the most recent by the time each await settles, so a stale result
        // never clobbers newer ones.
        const generation = ++this._fetchGeneration

        // The previous call's package-list request is now guaranteed stale (its
        // result would just be discarded by the generation check below) - abort it
        // so it stops competing with this newer request for a connection/server
        // time. Guarded by state() so we don't abort a request that's already
        // finished (which fetchPackages()'s shared cache may still be serving to
        // some other caller).
        if (this._pendingPackagesXhr && this._pendingPackagesXhr.state() === "pending") {
            this._pendingPackagesXhr.abort()
        }

        if(include_meta) {
            this.packageCount = await NetworkPropertySet.prototype.collectionLength(
                `/a/package`,
                this.packageRequestBody
            )
            if (generation !== this._fetchGeneration) return
            if(!(this.forUser || this.searchTerm)) {
                this.servicePackageCount = this.packageCount
            }
        }
        this.nonAllowancePackages = null
        if(non_skip) {
            this.page = 0
        }

        let result
        try {
            result = await this.fetchPackages(non_skip ? null : this.page * this.pageLength, this.pageLength)
        } catch (err) {
            // Our own abort() above (from a newer fetch() call) rejects this
            // request - that's expected, not an error, so swallow it quietly
            // rather than letting it surface as an unhandled rejection.
            if (err instanceof Object && "statusText" in err && err.statusText === "abort") return
            throw err
        }
        if (generation !== this._fetchGeneration) return

        // The grouped-multi fetch in fetchAllAllowances returns every managed_vps allowance
        // with its packages attached. Wait for it so byType.managed_vps is populated before
        // we read it — otherwise a slow grouped-multi can lose pseudoAllowance on every VPS
        // package on initial load.
        if (ServicesInfo._allowancesPromise) {
            await ServicesInfo._allowancesPromise
        }

        // Build a lookup of every managed VPS package, used to tag pseudoAllowance on
        // paginated /a/package rows so the "own account" bucket can exclude them
        // client-side (see non-allowance-detail.js's nonAllowancePackagesFiltered).
        // Cloud-Server cards themselves read directly from byType.managed_vps (see
        // non-allowance-detail.js), not from this paginated list, so a VPS package
        // sorting onto a later page never leaves its card incomplete.
        const managedVpsPackageById = {}
        if (this.byType.managed_vps && this.byType.managed_vps.length > 0) {
            for (const as of this.byType.managed_vps) {
                for (const pkg of as.packages) {
                    managedVpsPackageById[pkg.id] = pkg
                }
            }
        }

        const packages = result.map((p) => {
            if (managedVpsPackageById[p.id]) {
                p.pseudoAllowance = managedVpsPackageById[p.id].allowance
            }
            return new PackageService(p)
        })

        this.nonAllowancePackages = packages
    }

    fetchAllAllowances() {
        return ServicesInfo.fetchAllAllowancesCombined().then(data => {
            const wrap = (raws, Cls) => raws.map(r => {
                const allowance = new Cls(r)
                if (
                    allowance.packages &&
                    Array.isArray(allowance.packages) &&
                    allowance.packages.length
                ) {
                    allowance.packages.forEach(p => this.allowancePackages.push(p))
                }
                if (this.store) {
                    allowance.setStore(this.store)
                }
                return allowance
            })

            this.byType.package_allowance   = wrap(data.package_allowance,   PackageAllowanceService)
            this.byType.wordpress_allowance = wrap(data.wordpress_allowance, WordpressAllowanceService)
            this.byType.managed_vps         = wrap(data.managed_vps,         ManagedVpsAllowanceService)
            this.byType.autoscale_allowance = wrap(data.autoscale_allowance, AutoscaleAllowanceService)
        })
    }

    /**
     * @param {?number} skip
     * @param {?number} limit
     * @returns {Promise<packageIn[]>}
     */
    fetchPackages(skip, limit) {
        const cacheKey = `${skip},${limit},${JSON.stringify(this.packageRequestBody)},${this.sortField},${this.sortAscending}`

        // If cached, return immediately
        if (ServicesInfo._packagesCache[cacheKey]) {
            return ServicesInfo._packagesCache[cacheKey]
        }

        // Save the promise so concurrent calls don’t duplicate requests. Kept as
        // its own local so we can track the raw jqXHR (for our own abort() use
        // in fetch()) separately from the cached, .then()-chained promise -
        // chaining .then()/.catch() off a jqXHR returns a plain promise that no
        // longer exposes abort().
        const xhr = $.ajax({
            method: "get",
            url: `/a/package`,
            data: Object.assign(
                {
                    fields: [
                        "id",
                        "enabled",
                        "hasWebsiteTurbo",
                        "location",
                        "names",
                        "name",
                        "packageTypeName",
                        "productSpec",
                        "created",
                        "packageLabels",
                        "web.hasSsl",
                        "web.platform",
                        "websiteTurbo",
                        "isWordpress",
                        "includeInWordpressManager",
                    ],
                    limit: limit,
                    skip: skip,
                    sort: [
                        { [this.sortField]: this.sortAscending ? "asc" : "desc" }
                    ],
                },
                this.packageRequestBody
            ),
        })
        this._pendingPackagesXhr = xhr

        ServicesInfo._packagesCache[cacheKey] = xhr.then(result => {
            // Replace promise with resolved data
            ServicesInfo._packagesCache[cacheKey] = result
            return result
        }).catch(err => {
            // Clear cache on failure so retries can work
            delete ServicesInfo._packagesCache[cacheKey]
            throw err
        })

        return ServicesInfo._packagesCache[cacheKey]
    }

    /**
     * @param {string} string
     * @returns {string}
     */
    escapeRegexString(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }
    /**
     * Set store object
     * @param {Store} store
     */
    setStore(store) {
        this.store = store;
    }
}
